import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { produce } from 'immer';
import { DatasourceUpgradeStatus } from './upgrade/datasourceUpgrade.types';
import { Datasource_BronzeTable_Autoloader } from '@/types/api';

export type UiStatus = {
  completed: boolean;
  enableOnComplete: boolean;
  datasource?: core_v1_DataSource | undefined;
  upgradePresetVersion?: number | undefined;
  upgradeStatus?: DatasourceUpgradeStatus | undefined;
  autoloader?: Datasource_BronzeTable_Autoloader | undefined;
};

/**
 * The uiStatus allows us to store arbitrary data in the datasource metadata. When we save
 * an existing datasource without publishing changes, we store the datasource in uiStatus.datasource.
 * When we publish changes, we remove the datasource from uiStatus and write it to the actual datasource.
 * @param datasource - The datasource to get the uiStatus from
 * @returns The uiStatus
 */
export const getUiStatus = (datasource: core_v1_DataSource): UiStatus => {
  const uiStatusJson: string | undefined | null = datasource.metadata?.uiStatus;

  if (!uiStatusJson) {
    return {
      completed: false,
      enableOnComplete: false,
    };
  }

  try {
    const uiStatus = JSON.parse(uiStatusJson);
    return uiStatus;
  } catch (e) {
    console.error('Error parsing uiStatus:', e);
    return {
      completed: false,
      enableOnComplete: false,
    };
  }
};

/**
 * Set the uiStatus in the datasource metadata. We have to strip out the uiStatus from the datasource
 * in the uiStatus. If not the uiStatus continually nests itself and eventually is too large to store.
 * @param datasource - The datasource to set the uiStatus in
 * @param uiStatus - The uiStatus to set
 * @returns The datasource with the uiStatus set
 */
export const setUiStatus = (
  datasource: core_v1_DataSource,
  uiStatus: UiStatus,
): core_v1_DataSource => {
  const uiStatusClean = produce(uiStatus, (draft) => {
    if (draft.datasource?.metadata?.uiStatus) {
      draft.datasource.metadata.uiStatus = undefined;
    }
  });

  const uiStatusJson = JSON.stringify(uiStatusClean);
  const result = produce(datasource, (draft) => {
    if (!draft.metadata) {
      draft.metadata = {
        name: '',
      };
    }
    draft.metadata = {
      ...draft.metadata,
      uiStatus: uiStatusJson,
    };
  });
  return result;
};

/**
 * True if the datasource has been created on the review page.
 * @param datasource - The datasource to check if it is completed
 * @returns True if the datasource is completed, false otherwise
 */
export const isCompleted = (datasource: core_v1_DataSource): boolean => {
  const status = getUiStatus(datasource);
  return status.completed;
};

/**
 * Set the datasource to completed.
 * @param datasource - The datasource to set to completed
 * @returns The datasource with the completed status set
 */
export const setCompleted = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
    completed: true,
  };
  return setUiStatus(datasource, newStatus);
};

/**
 * Set the datasource to enable on completion.
 * @param datasource - The datasource to set to enable on complete
 * @returns The datasource with the enable on complete status set
 */
export const setEnableOnComplete = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
    enableOnComplete: true,
  };
  return setUiStatus(datasource, newStatus);
};

/**
 * Clear the enable on complete status.
 * @param datasource - The datasource to clear the enable on complete status from
 * @returns The datasource with the enable on complete status cleared
 */
export const clearEnableOnComplete = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
    enableOnComplete: false,
  };
  return setUiStatus(datasource, newStatus);
};

/**
 * True if the datasource should be enabled on completion.
 * @param datasource - The datasource to check if it should be enabled on completion
 * @returns True if the datasource should be enabled on completion, false otherwise
 */
export const shouldEnableOnComplete = (
  datasource: core_v1_DataSource,
): boolean => {
  const status = getUiStatus(datasource);
  return status.enableOnComplete;
};

/**
 * Set the unpublished changes in the datasource metadata.
 * @param datasource - The datasource to set the unpublished changes in
 * @param changes - The changes to set
 * @returns The datasource with the unpublished changes set
 */
export const setUnpublishedChanges = (
  datasource: core_v1_DataSource,
  changes: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
    datasource: changes,
  };
  return setUiStatus(datasource, newStatus);
};

/**
 * Get the unpublished changes from the datasource metadata.
 * @param datasource - The datasource to get the unpublished changes from
 * @returns The unpublished changes
 */
export const getUnpublishedChanges = (
  datasource: core_v1_DataSource,
): core_v1_DataSource | undefined => {
  const status = getUiStatus(datasource);
  const savedDatasource = status.datasource;
  if (savedDatasource) {
    savedDatasource.metadata!.version = datasource.metadata?.version;
    savedDatasource.metadata!.uiStatus = datasource.metadata?.uiStatus;
  }
  return savedDatasource;
};

/**
 * Clear the unpublished changes from the datasource metadata.
 * @param datasource - The datasource to clear the unpublished changes from
 * @returns The datasource with the unpublished changes cleared
 */
export const clearUnpublishedChanges = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
  };
  delete newStatus.datasource;
  return setUiStatus(datasource, newStatus);
};

/**
 * Set the upgrade preset version in the datasource metadata once an upgrade has been started.
 * @param datasource - The datasource to set the upgrade preset version in
 * @param version - The version to set
 * @returns The datasource with the upgrade preset version set
 */
export const setUpgradePresetVersion = (
  datasource: core_v1_DataSource,
  version: number,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
    upgradePresetVersion: version,
  };
  return setUiStatus(datasource, newStatus);
};

/**
 * Get the upgrade preset version from the datasource metadata if there is an upgrade in progress.
 * @param datasource - The datasource to get the upgrade preset version from
 * @returns The upgrade preset version
 */
export const getUpgradePresetVersion = (
  datasource: core_v1_DataSource,
): number | undefined => {
  const status = getUiStatus(datasource);
  return status.upgradePresetVersion;
};

/**
 * Clear the upgrade preset version from the datasource metadata.
 * @param datasource - The datasource to clear the upgrade preset version from
 * @returns The datasource with the upgrade preset version cleared
 */
export const clearUpgradePresetVersion = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
  };
  delete newStatus.upgradePresetVersion;
  return setUiStatus(datasource, newStatus);
};

/**
 * Set the upgrade status object in the datasource metadata.
 * @param datasource - The datasource to set the upgrade status object in
 * @param status - The status to set
 * @returns The datasource with the upgrade status object set
 */
export const setUpgradeStatusObject = (
  datasource: core_v1_DataSource,
  status: DatasourceUpgradeStatus,
): core_v1_DataSource => {
  const uiStatus = getUiStatus(datasource);
  const newStatus = {
    ...uiStatus,
    upgradeStatus: status,
  };
  return setUiStatus(datasource, newStatus);
};

/**
 * Get the upgrade status object from the datasource metadata if there is an upgrade in progress.
 * @param datasource - The datasource to get the upgrade status object from
 * @returns The upgrade status object
 */
export const getUpgradeStatusObject = (
  datasource: core_v1_DataSource,
): DatasourceUpgradeStatus | undefined => {
  const status = getUiStatus(datasource);
  return status.upgradeStatus;
};

export const clearUpgradeStatusObject = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const status = getUiStatus(datasource);
  const newStatus = {
    ...status,
  };
  delete newStatus.upgradeStatus;
  return setUiStatus(datasource, newStatus);
};

export const setAutoloader = (
  datasource: core_v1_DataSource,
  autoloader: Datasource_BronzeTable_Autoloader,
): core_v1_DataSource => {
  const uiStatus = getUiStatus(datasource);
  const newStatus = {
    ...uiStatus,
    autoloader,
  };
  return setUiStatus(datasource, newStatus);
};

export const getAutoloader = (
  datasource: core_v1_DataSource,
): Datasource_BronzeTable_Autoloader | undefined => {
  const uiStatus = getUiStatus(datasource);
  return uiStatus.autoloader;
};

export const clearAutoloader = (
  datasource: core_v1_DataSource,
): core_v1_DataSource => {
  const uiStatus = getUiStatus(datasource);
  const newStatus = {
    ...uiStatus,
  };
  delete newStatus.autoloader;
  return setUiStatus(datasource, newStatus);
};
